import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import ThemeToggle from '@/components/ThemeToggle';
import { 
  Zap, Shield, Bot, TrendingDown, ArrowRight, Check, X, 
  Smartphone, Lock, ChevronRight, Menu, Sparkles, Eye,
  Target, Crosshair, Radio, Cpu, Fingerprint, ScanLine, Scan,
  Skull, Clock, Mail, Building2, AlertTriangle, Crown,
  MessageSquare, Play, Pause, DollarSign, CreditCard, Bell, BarChart3, Heart
} from 'lucide-react';
import { ModeToggle } from '@/components/ThemeToggle';
import ExecutiveLanding from './ExecutiveLanding';

const Landing = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { mode } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [savingsCounter, setSavingsCounter] = useState(0);
  const [typedText, setTypedText] = useState('');
  const [activeFeature, setActiveFeature] = useState(0);
  const [zombieCount, setZombieCount] = useState(0);
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
  const fullText = 'RECURRING PAYMENT THREATS DETECTED';

  // Animated counter effect
  useEffect(() => {
    const target = 412;
    const duration = 2000;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setSavingsCounter(target);
        clearInterval(timer);
      } else {
        setSavingsCounter(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, []);

  // Zombie counter
  useEffect(() => {
    const target = 47892;
    const duration = 3000;
    const steps = 80;
    const increment = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setZombieCount(target);
        clearInterval(timer);
      } else {
        setZombieCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, []);

  // PWA Install prompt listener
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Typing effect
  useEffect(() => {
    let index = 0;
    const typeTimer = setInterval(() => {
      if (index <= fullText.length) {
        setTypedText(fullText.slice(0, index));
        index++;
      } else {
        clearInterval(typeTimer);
      }
    }, 80);
    return () => clearInterval(typeTimer);
  }, []);

  // Auto rotate features
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % 4);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  // Digital rain particles
  const particles = Array.from({ length: 40 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 5}s`,
    duration: `${4 + Math.random() * 4}s`,
    height: `${20 + Math.random() * 40}px`
  }));

  const operationalFeatures = [
    {
      icon: <Skull className="w-7 h-7" />,
      tag: "ZOMBIE KILLER",
      title: "Free Trial? We'll Kill It Before It Bites.",
      hook: "That 'free' Hulu trial charges $17.99 in 2 days. Sub-Zero sees it. Sub-Zero kills it. You save $215/year.",
      stat: "47,892",
      statLabel: "zombie trials killed this month",
      color: "destructive",
      details: [
        "Countdown timers on every trial — never miss the cancellation window",
        "Auto-Kill mode: we cancel it the second before they charge",
        "Works with any service — Netflix, Hulu, Adobe, gym memberships, all of them"
      ]
    },
    {
      icon: <Scan className="w-7 h-7" />,
      tag: "AUTO-DETECT",
      title: "We Find Trials You Forgot You Signed Up For.",
      hook: "Connect Gmail or bank. Our AI scans for trial emails, $0 charges, and signup receipts. Nothing slips through.",
      stat: "3.2",
      statLabel: "hidden trials found per user on average",
      color: "primary",
      details: [
        "Gmail scan: detects 'Your free trial has started' emails automatically",
        "Bank scan: catches $0.00 and $1.00 authorization charges from new signups",
        "Real-time alerts the second a new trial is detected"
      ]
    },
    {
      icon: <Crosshair className="w-7 h-7" />,
      tag: "AI KILL SWITCH",
      title: "One Tap. Subscription Dead.",
      hook: "No settings menus. No 'Are you sure?' loops. Press KILL. Our AI handles the rest in under 4 seconds.",
      stat: "\u003C 4s",
      statLabel: "average cancellation time",
      color: "destructive",
      details: [
        "Works on 500+ services including the sneaky ones that hide the cancel button",
        "No phone calls, no waiting on hold, no 'retention specialist' negotiations",
        "Instant confirmation — we screenshot the cancellation as proof"
      ]
    },
    {
      icon: <MessageSquare className="w-7 h-7" />,
      tag: "BILL NEGOTIATOR",
      title: "Our AI Calls Their AI. Your Bill Drops.",
      hook: "Internet too expensive? Insurance creeping up? Our agent contacts support 24/7, finds every discount, locks the lowest rate — while you sleep.",
      stat: "20%",
      statLabel: "average bill reduction",
      color: "primary",
      details: [
        "70% success rate on negotiations — higher than doing it yourself",
        "Works on ISPs, streaming, insurance, phone plans, and more",
        "No risk: we only apply offers you approve"
      ]
    }
  ];

  const executiveFeatures = [
    {
      icon: <Bell className="w-7 h-7" />,
      tag: "Smart Tracking",
      title: "Every Subscription, Beautifully Organized",
      hook: "Connect your email or bank and we'll automatically detect every recurring charge. No more surprises on your statement.",
      stat: "3.2",
      statLabel: "hidden subscriptions found per user",
      color: "sky",
      details: [
        "Automatic detection via email scanning and bank statement analysis",
        "Real-time alerts before trials convert to paid subscriptions",
        "Beautiful calendar view of all upcoming charges"
      ]
    },
    {
      icon: <CreditCard className="w-7 h-7" />,
      tag: "Easy Cancellation",
      title: "Cancel Any Service in One Click",
      hook: "No more navigating confusing settings pages. Our AI assistant handles the entire cancellation process for you in seconds.",
      stat: "< 4s",
      statLabel: "average cancellation time",
      color: "blue",
      details: [
        "Works with 500+ services — even the ones that make it difficult",
        "Automatic screenshot proof of every cancellation",
        "MFA-aware: seamlessly handles two-factor authentication"
      ]
    },
    {
      icon: <BarChart3 className="w-7 h-7" />,
      tag: "Bill Negotiation",
      title: "We Negotiate Lower Rates Automatically",
      hook: "Our AI contacts service providers on your behalf, finding discounts and locking in lower rates — while you focus on what matters.",
      stat: "20%",
      statLabel: "average bill reduction",
      color: "violet",
      details: [
        "70% success rate on negotiations — better than doing it yourself",
        "Works on internet, streaming, insurance, phone plans and more",
        "Zero risk: we only apply savings you approve"
      ]
    },
    {
      icon: <Shield className="w-7 h-7" />,
      tag: "Smart Insights",
      title: "Understand Your Spending, Effortlessly",
      hook: "Get clear, beautiful reports on where your money goes. See trends, identify waste, and make smarter decisions about your subscriptions.",
      stat: "$412",
      statLabel: "average saved per year",
      color: "emerald",
      details: [
        "Monthly and yearly spending breakdowns with visual charts",
        "Waste score analysis showing unused subscriptions",
        "5-year projection of potential savings"
      ]
    }
  ];

  // ── Executive mode → Prestige Landing ──
  if (mode === 'executive') return <ExecutiveLanding />;

  return (
    <div className="min-h-screen bg-background relative overflow-hidden" data-testid="landing-page">
      {/* Background Effects */}
      <div className="fixed inset-0 cyber-grid opacity-50 pointer-events-none" />
      <div className="fixed inset-0 hero-gradient pointer-events-none" />
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {particles.map(p => (
          <div key={p.id} className="digital-rain ghost-particle" style={{ left: p.left, animationDelay: p.delay, animationDuration: p.duration, height: p.height }} />
        ))}
      </div>
      <div className="fixed inset-0 scanline-bg pointer-events-none z-10" />

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 ${mode === 'executive' ? 'bg-white/85 backdrop-blur-xl border-b border-gray-100' : 'glass'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              {mode === 'executive' ? (
                <>
                  <div className="w-9 h-9 bg-gradient-to-br from-sky-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-sky-500/20">
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-display font-bold text-lg text-foreground">Sub-Zero</span>
                </>
              ) : (
                <>
                  <div className="w-10 h-10 bg-primary rounded flex items-center justify-center glow-primary">
                    <Zap className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <span className="font-display font-bold text-lg text-foreground tracking-tight">SUB-ZERO</span>
                    <span className="hidden sm:block text-[10px] font-mono text-primary tracking-widest">GHOST AUDITOR v2.0</span>
                  </div>
                </>
              )}
            </Link>

            <div className="hidden md:flex items-center gap-6">
              {mode === 'executive' ? (
                <>
                  <a href="#features" className="text-muted-foreground hover:text-foreground transition-colors text-sm font-medium">Features</a>
                  <a href="#how-it-works" className="text-muted-foreground hover:text-foreground transition-colors text-sm font-medium">How It Works</a>
                  <Link to="/pricing" className="text-muted-foreground hover:text-foreground transition-colors text-sm font-medium">Pricing</Link>
                </>
              ) : (
                <>
                  <a href="#features" className="text-muted-foreground hover:text-primary transition-colors text-sm font-mono">[{t('nav.features')}]</a>
                  <a href="#zombie" className="text-muted-foreground hover:text-destructive transition-colors text-sm font-mono">[ZOMBIE KILLER]</a>
                  <Link to="/pricing" className="text-muted-foreground hover:text-primary transition-colors text-sm font-mono">[{t('nav.pricing')}]</Link>
                </>
              )}
              <LanguageSelector variant="compact" />
              <ModeToggle compact />
              <ThemeToggle />
              {user ? (
                <Button onClick={() => navigate('/dashboard')} className={mode === 'executive' ? 'bg-sky-500 text-white hover:bg-sky-600 rounded-xl font-semibold shadow-lg shadow-sky-500/20' : 'bg-primary text-primary-foreground hover:bg-primary/90 rounded font-bold uppercase tracking-wide glow-primary'} data-testid="dashboard-nav-btn">
                  {mode === 'executive' ? <BarChart3 className="w-4 h-4 mr-2" /> : <Target className="w-4 h-4 mr-2" />}
                  {mode === 'executive' ? 'Dashboard' : 'Command Center'}
                </Button>
              ) : (
                <div className="flex items-center gap-3">
                  <Link to="/login">
                    <Button variant={mode === 'executive' ? 'ghost' : 'ghost'} className={mode === 'executive' ? 'text-foreground hover:bg-gray-100 rounded-xl font-medium' : 'text-foreground border border-white/20 hover:bg-white/10 rounded font-mono'} data-testid="login-nav-btn">
                      {mode === 'executive' ? 'Sign In' : 'LOGIN'}
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button className={mode === 'executive' ? 'bg-sky-500 text-white hover:bg-sky-600 rounded-xl font-semibold shadow-lg shadow-sky-500/20' : 'bg-primary text-primary-foreground hover:bg-primary/90 rounded font-bold uppercase tracking-wide glow-action'} data-testid="get-started-nav-btn">
                      {mode === 'executive' ? 'Get Started' : 'DEPLOY AGENT'}
                    </Button>
                  </Link>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 md:hidden">
              <LanguageSelector variant="compact" />
              <ModeToggle compact />
              <ThemeToggle />
              <button className="text-foreground p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className={`md:hidden border-t ${mode === 'executive' ? 'bg-white border-gray-100' : 'glass border-border'}`}>
            <div className="px-4 py-4 space-y-3">
              {mode === 'executive' ? (
                <>
                  <a href="#features" className="block text-muted-foreground hover:text-foreground py-2 font-medium">Features</a>
                  <a href="#how-it-works" className="block text-muted-foreground hover:text-foreground py-2 font-medium">How It Works</a>
                  <Link to="/pricing" className="block text-muted-foreground hover:text-foreground py-2 font-medium">Pricing</Link>
                </>
              ) : (
                <>
                  <a href="#features" className="block text-muted-foreground hover:text-primary py-2 font-mono">[FEATURES]</a>
                  <a href="#zombie" className="block text-muted-foreground hover:text-destructive py-2 font-mono">[ZOMBIE KILLER]</a>
                  <Link to="/pricing" className="block text-muted-foreground hover:text-primary py-2 font-mono">[PRICING]</Link>
                </>
              )}
              {user ? (
                <Button onClick={() => navigate('/dashboard')} className={`w-full ${mode === 'executive' ? 'bg-sky-500 text-white rounded-xl' : 'bg-primary text-primary-foreground rounded font-bold'}`} data-testid="dashboard-mobile-btn">
                  {mode === 'executive' ? 'Dashboard' : 'Command Center'}
                </Button>
              ) : (
                <div className="space-y-2">
                  <Link to="/login" className="block"><Button variant="outline" className={`w-full ${mode === 'executive' ? 'rounded-xl' : 'rounded font-mono'}`} data-testid="login-mobile-btn">{mode === 'executive' ? 'Sign In' : 'LOGIN'}</Button></Link>
                  <Link to="/register" className="block"><Button className={`w-full ${mode === 'executive' ? 'bg-sky-500 text-white rounded-xl' : 'bg-primary text-primary-foreground rounded font-bold'}`} data-testid="register-mobile-btn">{mode === 'executive' ? 'Get Started' : 'DEPLOY AGENT'}</Button></Link>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* ===== HERO SECTION ===== */}
      {mode === 'executive' ? (
        /* ===== EXECUTIVE HERO ===== */
        <section className="relative pt-28 sm:pt-36 pb-20 sm:pb-32 px-4 sm:px-6 lg:px-8 exec-hero-gradient">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <div className="space-y-8 relative z-20">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-50 border border-sky-200 rounded-full animate-fade-up">
                  <Sparkles className="w-4 h-4 text-sky-500" />
                  <span className="text-sky-600 text-sm font-medium">Smart Subscription Management</span>
                </div>

                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-[1.1] animate-fade-up" style={{animationDelay:'0.1s'}}>
                  Take Control<br />of Every<br /><span className="bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent">Subscription.</span>
                </h1>

                <p className="text-lg sm:text-xl text-muted-foreground max-w-lg leading-relaxed animate-fade-up" style={{animationDelay:'0.2s'}}>
                  Track, manage, and optimize all your recurring payments in one beautiful place. Save an average of <span className="text-foreground font-semibold">${savingsCounter}/year</span>.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 animate-fade-up" style={{animationDelay:'0.3s'}}>
                  <Button
                    size="lg"
                    onClick={() => navigate(user ? '/dashboard' : '/register')}
                    className="bg-sky-500 text-white hover:bg-sky-600 rounded-2xl font-semibold text-lg px-8 py-6 shadow-xl shadow-sky-500/25 transition-all hover:shadow-2xl hover:shadow-sky-500/30 hover:-translate-y-0.5"
                    data-testid="hero-cta-btn"
                  >
                    Start Saving — Free
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                    className="border-gray-200 text-foreground hover:bg-gray-50 rounded-2xl font-medium text-lg px-8 py-6"
                    data-testid="hero-features-btn"
                  >
                    See How It Works
                  </Button>
                </div>

                <div className="flex flex-wrap items-center gap-6 pt-2 animate-fade-up" style={{animationDelay:'0.4s'}}>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center">
                      <Shield className="w-4 h-4 text-emerald-500" />
                    </div>
                    <span className="text-sm text-muted-foreground">Bank-grade security</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-sky-50 flex items-center justify-center">
                      <Lock className="w-4 h-4 text-sky-500" />
                    </div>
                    <span className="text-sm text-muted-foreground">256-bit encrypted</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-violet-50 flex items-center justify-center">
                      <Heart className="w-4 h-4 text-violet-500" />
                    </div>
                    <span className="text-sm text-muted-foreground">Loved by thousands</span>
                  </div>
                </div>
              </div>

              {/* Executive Right Card */}
              <div className="relative z-20 animate-fade-right" style={{animationDelay:'0.2s'}}>
                <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-2xl shadow-gray-200/50 border border-gray-100 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-sky-100 to-transparent rounded-bl-full opacity-60" />
                  
                  <div className="flex items-center justify-between mb-6 relative">
                    <div>
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Annual Savings</p>
                      <span className="text-5xl font-bold bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent">${savingsCounter}</span>
                    </div>
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                      <TrendingDown className="w-6 h-6 text-white" />
                    </div>
                  </div>

                  <div className="space-y-3 relative">
                    {[
                      { name: 'Netflix', price: '$15.49', status: 'Unused 21 days', color: 'red', icon: 'N' },
                      { name: 'Adobe CC', price: '$54.99', status: 'Auto-renewing', color: 'amber', icon: 'A' },
                      { name: 'Spotify', price: '$9.99', status: 'Active', color: 'emerald', icon: 'S' },
                    ].map((item, i) => (
                      <div key={i} className={`flex items-center justify-between p-3 rounded-2xl border border-gray-100 hover:border-gray-200 transition-all hover:shadow-sm`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm ${
                            item.color === 'red' ? 'bg-red-500' : item.color === 'amber' ? 'bg-amber-500' : 'bg-emerald-500'
                          }`}>{item.icon}</div>
                          <div>
                            <p className="font-semibold text-foreground text-sm">{item.name}</p>
                            <p className={`text-xs ${item.color === 'red' ? 'text-red-500' : item.color === 'amber' ? 'text-amber-500' : 'text-emerald-500'}`}>{item.status}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-foreground text-sm">{item.price}<span className="text-muted-foreground text-xs">/mo</span></p>
                          {item.color === 'red' && (
                            <span className="text-xs px-2 py-0.5 bg-red-50 text-red-500 rounded-full font-medium">Cancel</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : (
      /* ===== OPERATIVE HERO ===== */
      <section className="relative pt-28 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Elevated Command Terminal — the hero feels like a floating display panel */}
          <div className="hero-terminal-card rounded-lg p-6 sm:p-10 relative overflow-hidden">
            {/* Terminal chrome — top bar */}
            <div className="absolute top-0 left-0 right-0 h-1 hero-terminal-accent" />
            <div className="absolute bottom-0 left-0 right-0 h-px hero-terminal-accent opacity-40" />
            <div className="absolute top-0 left-0 bottom-0 w-px hero-terminal-accent opacity-30" />
            <div className="absolute top-0 right-0 bottom-0 w-px hero-terminal-accent opacity-30" />

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Left - Copy */}
            <div className="space-y-6 sm:space-y-8 relative z-20">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-destructive/10 border border-destructive/30 rounded animate-fade-up stagger-1 ghost-threat-bar">
                <span className="w-2 h-2 bg-destructive rounded-full ghost-dot"></span>
                <span className="text-destructive text-sm font-mono tracking-wider cursor-blink ghost-alert-text">{typedText}</span>
              </div>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-[1.1] animate-fade-up stagger-2 ghost-headline">
                {t('hero.title_1')} <span className="text-primary dark:text-primary text-destructive" style={{color:'var(--robbing-color)'}}>{t('hero.title_2')}</span> {t('hero.title_3')}
              </h1>

              <p className="text-lg sm:text-xl text-muted-foreground/80 font-semibold tracking-tight animate-fade-up stagger-2" style={{animationDelay:'0.15s'}}>
                {t('hero.subtitle')}
              </p>

              <p className="text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed animate-fade-up stagger-3">
                {t('hero.description', { amount: '$412' })}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 animate-fade-up stagger-4">
                <Button 
                  size="lg"
                  onClick={() => navigate(user ? '/dashboard' : '/register')}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded font-bold uppercase tracking-wide text-lg px-8 glow-action group"
                  data-testid="hero-cta-btn"
                >
                  <ScanLine className="w-5 h-5 mr-2 group-hover:animate-pulse" />
                  {t('hero.cta')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => {
                    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="border-white/20 text-foreground hover:bg-white/10 rounded font-mono"
                  data-testid="hero-features-btn"
                >
                  {t('hero.secondary_cta')}
                </Button>
              </div>

              {/* Trust + Stats Row */}
              <div className="flex flex-wrap items-center gap-x-8 gap-y-3 pt-2">
                <div className="flex items-center gap-2">
                  <Fingerprint className="w-5 h-5 text-primary" />
                  <span className="text-sm text-muted-foreground font-mono">{t('hero.encrypted')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary" />
                  <span className="text-sm text-muted-foreground font-mono">{t('hero.security')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Skull className="w-4 h-4 text-destructive" />
                  <span className="text-sm text-muted-foreground font-mono"><span className="text-destructive">{zombieCount.toLocaleString()}</span> {t('hero.zombies_killed')}</span>
                </div>
              </div>
            </div>

            {/* Right - Visualization */}
            <div className="relative z-20 animate-fade-right stagger-3">
              <div className="bg-card/80 backdrop-blur-xl border border-primary/30 rounded p-5 sm:p-6 noise-texture relative overflow-hidden hover-lift ghost-border">
                <div className="absolute inset-0 shimmer pointer-events-none" />
                
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <Cpu className="w-5 h-5 text-primary animate-pulse" />
                    <span className="text-xs font-mono text-primary tracking-widest">THREAT ANALYSIS COMPLETE</span>
                  </div>
                  <span className="text-xs font-mono text-muted-foreground">ID: #SZ-2024</span>
                </div>

                <div className="text-center mb-5 py-4 border-y border-border">
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-widest mb-2">PROJECTED ANNUAL RECOVERY</p>
                  <span className="text-5xl sm:text-6xl font-display font-bold text-primary text-glow-primary">${savingsCounter}</span>
                  <p className="text-sm text-muted-foreground mt-2 font-mono">avg. user extraction rate</p>
                </div>

                {/* Mock threat items */}
                <div className="space-y-3 mb-4">
                  <div className="bg-destructive/10 border border-destructive/30 rounded p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-red-600 rounded flex items-center justify-center text-white font-bold text-sm">N</div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-foreground text-sm">Netflix</p>
                          <span className="px-1.5 py-0.5 bg-destructive/20 text-destructive text-[10px] font-mono rounded">HIGH WASTE</span>
                        </div>
                        <p className="text-[10px] text-destructive font-mono">INACTIVE: 21 DAYS</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-sm text-foreground">$15.49<span className="text-muted-foreground text-xs">/mo</span></p>
                      <div className="mt-1 px-2 py-1 bg-destructive text-destructive-foreground rounded text-[10px] font-bold uppercase text-center kill-switch cursor-pointer">KILL SWITCH</div>
                    </div>
                  </div>

                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-yellow-600 rounded flex items-center justify-center text-white font-bold text-sm">
                        <Skull className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-foreground text-sm">Adobe CC Trial</p>
                          <span className="px-1.5 py-0.5 bg-yellow-500/20 text-yellow-500 text-[10px] font-mono rounded">ZOMBIE</span>
                        </div>
                        <p className="text-[10px] text-yellow-500 font-mono">CHARGES IN 2 DAYS - $54.99</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="mt-1 px-2 py-1 bg-yellow-500 text-black rounded text-[10px] font-bold uppercase text-center cursor-pointer">AUTO-KILL</div>
                    </div>
                  </div>
                </div>

                {/* Waste Score */}
                <div className="space-y-2">
                  <div className="flex justify-between text-xs font-mono">
                    <span className="text-muted-foreground">THREAT LEVEL</span>
                    <span className="text-destructive">65% WASTE DETECTED</span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full waste-meter-gradient rounded-full transition-all duration-1000" style={{ width: '65%' }} />
                  </div>
                </div>
              </div>

              <div className="absolute -top-4 -right-4 w-20 h-20 bg-primary/20 rounded-full blur-3xl" />
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-destructive/20 rounded-full blur-3xl" />
            </div>
          </div>
          </div> {/* End hero-terminal-card */}
        </div>
      </section>
      )}

      {/* ===== PROBLEM STATEMENT — THE BLEED ===== */}
      <section className={`py-16 sm:py-20 px-4 sm:px-6 lg:px-8 relative z-20 ${mode === 'executive' ? 'border-t border-gray-100' : 'border-t border-border/50'}`}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className={`text-xs tracking-widest ${mode === 'executive' ? 'text-sky-500 font-medium uppercase' : 'font-mono text-destructive'}`}>{mode === 'executive' ? 'THE PROBLEM' : '// THE PROBLEM'}</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mt-3 mb-4">
              {mode === 'executive' ? (
                <>You're Paying for Things You <span className="text-sky-500">Don't Use</span></>
              ) : (
                <>You're Paying for Things You <span className="text-destructive">Don't Use</span></>
              )}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-base">
              The average person has 12 subscriptions and forgets about 3 of them. That "free trial" you forgot? It's been charging you for months.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-6">
            {mode === 'executive' ? (
              <>
                <div className="bg-white rounded-2xl p-6 text-center shadow-lg shadow-red-500/5 border border-red-100 hover:shadow-xl transition-shadow">
                  <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4"><DollarSign className="w-6 h-6 text-red-500" /></div>
                  <p className="text-4xl font-bold text-red-500 mb-2">$412</p>
                  <p className="text-sm text-muted-foreground font-medium">avg wasted per year</p>
                </div>
                <div className="bg-white rounded-2xl p-6 text-center shadow-lg shadow-amber-500/5 border border-amber-100 hover:shadow-xl transition-shadow">
                  <div className="w-12 h-12 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-4"><Bell className="w-6 h-6 text-amber-500" /></div>
                  <p className="text-4xl font-bold text-amber-500 mb-2">73%</p>
                  <p className="text-sm text-muted-foreground font-medium">of free trials auto-convert</p>
                </div>
                <div className="bg-white rounded-2xl p-6 text-center shadow-lg shadow-sky-500/5 border border-sky-100 hover:shadow-xl transition-shadow">
                  <div className="w-12 h-12 rounded-2xl bg-sky-50 flex items-center justify-center mx-auto mb-4"><Zap className="w-6 h-6 text-sky-500" /></div>
                  <p className="text-4xl font-bold text-sky-500 mb-2">{"< 4s"}</p>
                  <p className="text-sm text-muted-foreground font-medium">to cancel any subscription</p>
                </div>
              </>
            ) : (
              <>
            <div className="bg-card/50 backdrop-blur border border-destructive/30 rounded p-6 text-center noise-texture relative overflow-hidden hover-lift ghost-border ghost-border-delay-1">
              <div className="absolute top-0 left-0 right-0 h-1 bg-destructive" />
              <p className="text-4xl font-display font-bold text-destructive mb-2">$412</p>
              <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">avg wasted per year</p>
              <p className="text-sm text-muted-foreground mt-3">on subscriptions people forget about</p>
            </div>
            <div className="bg-card/50 backdrop-blur border border-yellow-500/30 rounded p-6 text-center noise-texture relative overflow-hidden hover-lift ghost-border ghost-border-delay-2">
              <div className="absolute top-0 left-0 right-0 h-1 bg-yellow-500" />
              <p className="text-4xl font-display font-bold text-yellow-500 mb-2">73%</p>
              <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">of free trials</p>
              <p className="text-sm text-muted-foreground mt-3">auto-convert to paid without users knowing</p>
            </div>
            <div className="bg-card/50 backdrop-blur border border-primary/30 rounded p-6 text-center noise-texture relative overflow-hidden hover-lift ghost-border ghost-border-delay-3">
              <div className="absolute top-0 left-0 right-0 h-1 bg-primary" />
              <p className="text-4xl font-display font-bold text-primary mb-2">{"< 4s"}</p>
              <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">sub-zero cancels in</p>
              <p className="text-sm text-muted-foreground mt-3">what takes you 15 minutes of clicking</p>
            </div>
              </>
            )}
          </div>

          {/* 5-Year Projected Loss — Conversion Scare */}
          <div className="mt-10 bg-card/60 backdrop-blur-xl border border-destructive/30 rounded p-6 sm:p-8 noise-texture relative overflow-hidden ghost-loss-pulse" data-testid="landing-5yr-loss">
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-destructive to-transparent animate-pulse" />
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-destructive/50 to-transparent" />
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="text-center sm:text-left">
                <div className="flex items-center gap-2 mb-2 justify-center sm:justify-start">
                  <TrendingDown className="w-4 h-4 text-destructive" />
                  <span className="text-[10px] font-mono text-destructive uppercase tracking-widest">Projected 5-Year Bleed</span>
                </div>
                <p className="text-5xl sm:text-6xl font-display font-bold text-destructive text-glow-destructive">$24,720</p>
                <p className="text-xs font-mono text-muted-foreground mt-2">
                  That's what the average person bleeds on forgotten subs over 5 years.
                </p>
                <p className="text-xs font-mono text-yellow-500 mt-1">
                  $2,060 of that is going to zombie subscriptions you don't even use.
                </p>
              </div>
              <div className="text-center sm:text-right">
                <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest mb-2">What Sub-Zero agents recover</p>
                <p className="text-4xl font-display font-bold text-primary text-glow-primary">$10,300</p>
                <p className="text-xs text-muted-foreground font-mono mt-1">avg 5-year savings per agent</p>
                <Button
                  onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                  className="mt-4 bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded font-bold text-xs glow-action kill-switch"
                  data-testid="landing-5yr-cta"
                >
                  Stop the Bleed — Deploy Agent
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FEATURES — DEEP DIVE ===== */}
      <section id="features" className={`py-16 sm:py-24 px-4 sm:px-6 lg:px-8 relative z-20 ${mode === 'executive' ? '' : ''}`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className={`text-xs tracking-widest ${mode === 'executive' ? 'text-sky-500 font-medium uppercase' : 'font-mono text-primary'}`}>{mode === 'executive' ? 'FEATURES' : '// OPERATIONAL PROTOCOLS'}</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mt-3 mb-4">
              {mode === 'executive' ? (
                <>Everything You Need to <span className="bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent">Save Smarter</span></>
              ) : (
                <>Four Agents. One Mission. <span className="text-primary">Save Your Money.</span></>
              )}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {mode === 'executive' ? 'Powerful tools, beautifully simple.' : 'Four AI agents. One mission: save your money.'}
            </p>
          </div>

          {mode === 'executive' ? (
            /* Executive: Clean card grid */
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {executiveFeatures.map((f, idx) => (
                <div key={idx} className="bg-white rounded-3xl p-6 border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group" data-testid={`exec-feature-${idx}`}>
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 transition-colors ${
                    f.color === 'sky' ? 'bg-sky-50 text-sky-500 group-hover:bg-sky-100' :
                    f.color === 'blue' ? 'bg-blue-50 text-blue-500 group-hover:bg-blue-100' :
                    f.color === 'violet' ? 'bg-violet-50 text-violet-500 group-hover:bg-violet-100' :
                    'bg-emerald-50 text-emerald-500 group-hover:bg-emerald-100'
                  }`}>{f.icon}</div>
                  <h3 className="font-bold text-foreground text-lg mb-2">{f.tag}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">{f.hook}</p>
                  <div className="pt-4 border-t border-gray-100">
                    <span className={`text-3xl font-bold ${
                      f.color === 'sky' ? 'text-sky-500' :
                      f.color === 'blue' ? 'text-blue-500' :
                      f.color === 'violet' ? 'text-violet-500' :
                      'text-emerald-500'
                    }`}>{f.stat}</span>
                    <p className="text-xs text-muted-foreground mt-1">{f.statLabel}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
          <>
          {/* Feature Selector Tabs */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-10">
            {operationalFeatures.map((f, idx) => (
              <button
                key={idx}
                onClick={() => setActiveFeature(idx)}
                className={`px-4 py-2 rounded text-xs sm:text-sm font-mono font-bold uppercase tracking-wider transition-all duration-300 ${
                  activeFeature === idx
                    ? f.color === 'destructive'
                      ? 'bg-destructive text-destructive-foreground glow-destructive'
                      : 'bg-primary text-primary-foreground glow-primary'
                    : 'bg-card border border-border text-muted-foreground hover:text-foreground hover:border-primary/50'
                }`}
                data-testid={`feature-tab-${idx}`}
              >
                {f.tag}
              </button>
            ))}
          </div>

          {/* Active Feature Display */}
          <div className="grid lg:grid-cols-5 gap-8 items-start">
            {/* Left - Details (3 cols) */}
            <div className="lg:col-span-3 space-y-6">
              <div className={`inline-flex items-center gap-3 ${
                operationalFeatures[activeFeature].color === 'destructive' ? 'text-destructive' : 'text-primary'
              }`}>
                {operationalFeatures[activeFeature].icon}
                <span className="text-xs font-mono tracking-widest uppercase">
                  {operationalFeatures[activeFeature].tag}
                </span>
              </div>

              <h3 className="font-display text-2xl sm:text-3xl font-bold text-foreground leading-tight">
                {operationalFeatures[activeFeature].title}
              </h3>

              <p className="text-muted-foreground text-base leading-relaxed">
                {operationalFeatures[activeFeature].hook}
              </p>

              <div className="space-y-3 pt-2">
                {operationalFeatures[activeFeature].details.map((detail, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <Check className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                      operationalFeatures[activeFeature].color === 'destructive' ? 'text-destructive' : 'text-primary'
                    }`} />
                    <span className="text-sm text-foreground">{detail}</span>
                  </div>
                ))}
              </div>

              <Button 
                size="lg"
                onClick={() => navigate(user ? '/dashboard' : '/register')}
                className={`rounded font-bold uppercase tracking-wide ${
                  operationalFeatures[activeFeature].color === 'destructive'
                    ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                    : 'bg-primary text-primary-foreground hover:bg-primary/90 glow-primary'
                }`}
                data-testid="feature-cta-btn"
              >
                <Target className="w-4 h-4 mr-2" />
                Deploy This Agent
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>

            {/* Right - Stat Card (2 cols) */}
            <div className="lg:col-span-2">
              <div className={`bg-card/80 backdrop-blur-xl border rounded p-6 noise-texture text-center ${
                operationalFeatures[activeFeature].color === 'destructive'
                  ? 'border-destructive/40'
                  : 'border-primary/40'
              }`}>
                <p className={`text-6xl sm:text-7xl font-display font-bold mb-3 ${
                  operationalFeatures[activeFeature].color === 'destructive'
                    ? 'text-destructive text-glow-destructive'
                    : 'text-primary text-glow-primary'
                }`}>
                  {operationalFeatures[activeFeature].stat}
                </p>
                <p className="text-sm font-mono text-muted-foreground uppercase tracking-wider">
                  {operationalFeatures[activeFeature].statLabel}
                </p>
              </div>

              {/* Feature quick list */}
              <div className="mt-4 space-y-2">
                {operationalFeatures.map((f, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveFeature(idx)}
                    className={`w-full flex items-center gap-3 p-3 rounded transition-all text-left ${
                      activeFeature === idx
                        ? 'bg-card border border-primary/30'
                        : 'hover:bg-card/50'
                    }`}
                    data-testid={`feature-list-${idx}`}
                  >
                    <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${
                      f.color === 'destructive' ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'
                    }`}>
                      {f.icon}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{f.tag}</p>
                      <p className="text-xs text-muted-foreground truncate max-w-[200px]">{f.title}</p>
                    </div>
                    {activeFeature === idx && (
                      <ChevronRight className="w-4 h-4 text-primary ml-auto flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
          </>
          )}
        </div>
      </section>

      {/* ===== ZOMBIE TRIAL DEEP DIVE ===== */}
      {mode !== 'executive' && (
      <section id="zombie" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-destructive/5 border-y border-destructive/20 relative z-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-destructive/10 border border-destructive/30 rounded">
                <Skull className="w-4 h-4 text-destructive" />
                <span className="text-xs font-mono text-destructive tracking-widest">ZOMBIE TRIAL PROTECTION</span>
              </div>

              <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground leading-tight">
                "I'll Cancel It Before the Trial Ends" — <span className="text-destructive">You Won't.</span>
              </h2>

              <p className="text-muted-foreground text-base leading-relaxed">
                Be honest: how many times have you said "I'll cancel it before it charges"? Sub-Zero removes the human element.
                Sign up for a trial → we start a countdown → when it hits zero, we cancel it. Automatically.
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-card/80 border border-border rounded">
                  <div className="w-10 h-10 bg-destructive/10 border border-destructive/30 rounded flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-destructive" />
                  </div>
                  <div>
                    <h4 className="font-display font-bold text-foreground text-sm">Manual Tracking (Coffee Plan)</h4>
                    <p className="text-xs text-muted-foreground mt-1">Add any trial manually. We track the countdown and auto-kill it before they charge you.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-card/80 border border-yellow-500/30 rounded">
                  <div className="w-10 h-10 bg-yellow-500/10 border border-yellow-500/30 rounded flex items-center justify-center flex-shrink-0">
                    <Eye className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div>
                    <h4 className="font-display font-bold text-foreground text-sm flex items-center gap-2">
                      Auto-Detect (Shark Plan) <Crown className="w-3.5 h-3.5 text-yellow-500" />
                    </h4>
                    <p className="text-xs text-muted-foreground mt-1">Connect Gmail or bank. We automatically find trials you signed up for — even the ones you forgot about.</p>
                  </div>
                </div>
              </div>

              <Button 
                size="lg"
                onClick={() => navigate(user ? '/dashboard' : '/register')}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded font-bold uppercase tracking-wide kill-switch glow-action"
                data-testid="zombie-cta-btn"
                style={{boxShadow:'0 0 15px rgba(255,42,42,0.35)',filter:'drop-shadow(0 0 5px rgba(255,42,42,0.3))'}}
              >
                <Skull className="w-5 h-5 mr-2" />
                Activate Zombie Protection
              </Button>
            </div>

            {/* Right — Visual Demo */}
            <div className="space-y-4">
              {/* Simulated trial card */}
              <div className="bg-card border border-destructive/40 rounded p-5 noise-texture relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-1 bg-destructive animate-pulse" />
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-mono text-destructive tracking-widest flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> ZOMBIE TRIAL DETECTED
                  </span>
                  <span className="text-xs font-mono text-muted-foreground">AUTO-KILL: ON</span>
                </div>

                <div className="space-y-4">
                  {[
                    { name: "Hulu Plus", days: 2, price: 17.99, urgency: "critical" },
                    { name: "Adobe Creative Cloud", days: 5, price: 54.99, urgency: "warning" },
                    { name: "Cursor Pro", days: 11, price: 20.00, urgency: "safe" },
                  ].map((trial, idx) => (
                    <div key={idx} className={`flex items-center justify-between p-3 rounded border ${
                      trial.urgency === 'critical' ? 'border-destructive/50 bg-destructive/5' :
                      trial.urgency === 'warning' ? 'border-yellow-500/50 bg-yellow-500/5' :
                      'border-primary/30 bg-primary/5'
                    }`}>
                      <div>
                        <p className="font-medium text-foreground text-sm">{trial.name}</p>
                        <p className={`text-xs font-mono ${
                          trial.urgency === 'critical' ? 'text-destructive' :
                          trial.urgency === 'warning' ? 'text-yellow-500' : 'text-primary'
                        }`}>
                          {trial.days} days until charge
                        </p>
                      </div>
                      <div className="text-right flex items-center gap-3">
                        <span className="font-mono text-sm text-foreground">${trial.price}<span className="text-muted-foreground text-xs">/mo</span></span>
                        <div className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${
                          trial.urgency === 'critical' ? 'bg-destructive text-destructive-foreground kill-switch' :
                          trial.urgency === 'warning' ? 'bg-yellow-500 text-black' :
                          'bg-primary/20 text-primary'
                        }`}>
                          {trial.urgency === 'safe' ? 'TRACKED' : 'KILL'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-3 border-t border-border flex items-center justify-between">
                  <span className="text-xs text-muted-foreground font-mono">POTENTIAL MONTHLY SAVINGS</span>
                  <span className="text-lg font-display font-bold text-primary text-glow-primary">$92.98</span>
                </div>
              </div>

              <div className="bg-primary/5 border border-primary/20 rounded p-4 text-center">
                <p className="text-sm text-foreground font-medium">Sub-Zero has saved users <span className="text-primary font-bold font-mono">$2.4M</span> from zombie trials this year alone.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ===== HOW IT WORKS ===== */}
      <section id="how-it-works" className={`py-16 sm:py-24 px-4 sm:px-6 lg:px-8 relative z-20 ${mode === 'executive' ? '' : ''}`}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className={`text-xs tracking-widest ${mode === 'executive' ? 'text-sky-500 font-medium uppercase' : 'font-mono text-primary'}`}>{mode === 'executive' ? 'HOW IT WORKS' : '// DEPLOYMENT SEQUENCE'}</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mt-3 mb-4">
              {mode === 'executive' ? (
                <>3 Steps to <span className="bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent">Start Saving</span></>
              ) : (
                <>3 Steps. 60 Seconds. <span className="text-primary">Done.</span></>
              )}
            </h2>
          </div>

          <div className="grid sm:grid-cols-3 gap-8">
            {(mode === 'executive' ? [
              { step: "1", icon: <Scan className="w-6 h-6 text-sky-500" />, title: "Connect", desc: "Link your email or bank account. We'll find every subscription automatically.", color: 'sky' },
              { step: "2", icon: <BarChart3 className="w-6 h-6 text-blue-500" />, title: "Review", desc: "See all your subscriptions in one place. We'll highlight ones you can save on.", color: 'blue' },
              { step: "3", icon: <Zap className="w-6 h-6 text-emerald-500" />, title: "Save", desc: "Cancel unused services, negotiate lower rates, and watch your savings grow.", color: 'emerald' },
            ] : [
              { step: "01", icon: <ScanLine className="w-6 h-6 text-primary" />, title: "Connect & Scan", desc: "Link your bank or Gmail. We map every subscription and trial in seconds." },
              { step: "02", icon: <Eye className="w-6 h-6 text-destructive" />, title: "Review Threats", desc: "See your waste score, zombie trials, and unused subs — ranked by how much they cost you." },
              { step: "03", icon: <Crosshair className="w-6 h-6 text-primary" />, title: "Kill or Negotiate", desc: "One tap to cancel. Or let our AI negotiate a lower rate. Save money without lifting a finger." }
            ]).map((item, idx) => (
              mode === 'executive' ? (
                <div key={idx} className="bg-white rounded-3xl p-8 border border-gray-100 shadow-lg text-center hover:shadow-xl transition-all group">
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 ${
                    item.color === 'sky' ? 'bg-sky-50 group-hover:bg-sky-100' :
                    item.color === 'blue' ? 'bg-blue-50 group-hover:bg-blue-100' :
                    'bg-emerald-50 group-hover:bg-emerald-100'
                  }`}>{item.icon}</div>
                  <div className={`text-xs font-medium mb-2 ${
                    item.color === 'sky' ? 'text-sky-500' : item.color === 'blue' ? 'text-blue-500' : 'text-emerald-500'
                  }`}>Step {item.step}</div>
                  <h3 className="font-bold text-xl text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm">{item.desc}</p>
                </div>
              ) : (
              <div key={idx} className="relative">
                <div className="bg-card/50 backdrop-blur border border-border rounded p-6 noise-texture hover:border-primary/50 transition-all h-full">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl font-display font-bold text-border">{item.step}</span>
                    <div className="w-10 h-10 bg-primary/10 border border-primary/30 rounded flex items-center justify-center">
                      {item.icon}
                    </div>
                  </div>
                  <h3 className="font-display text-lg font-bold text-foreground mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
                {idx < 2 && (
                  <div className="hidden sm:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-6 h-6 text-border" />
                  </div>
                )}
              </div>
              )
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECURITY ===== */}
      <section id="security" className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-card/30 relative z-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <span className="text-xs font-mono text-primary tracking-widest">// SECURITY CLEARANCE</span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground">
                Your Data is <span className="text-primary">Classified</span>
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground">
                We never see your passwords. AI agents run in sandboxed environments with military-grade encryption.
              </p>
              <div className="space-y-4">
                {[
                  { icon: <Fingerprint className="w-5 h-5 text-primary" />, title: "256-bit Bank-Level Encryption", desc: "All data encrypted via industry-standard integration." },
                  { icon: <Cpu className="w-5 h-5 text-primary" />, title: "Sandboxed AI Agents", desc: "Isolated environments with zero data retention." },
                  { icon: <Lock className="w-5 h-5 text-primary" />, title: "Zero Data Sale Policy", desc: "Your data is never monetized. Period." }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-4 bg-card/50 border border-border rounded">
                    <div className="w-10 h-10 bg-primary/10 border border-primary/30 rounded flex items-center justify-center flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="font-display font-bold text-foreground text-sm">{item.title}</h4>
                      <p className="text-xs text-muted-foreground mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="install-prompt rounded-lg p-8 text-center">
              <Smartphone className="w-16 h-16 text-primary mx-auto mb-4" />
              <h3 className="font-display text-2xl font-bold text-foreground mb-2">Deploy to Device</h3>
              <p className="text-muted-foreground mb-6 text-sm">
                Install on your home screen for instant access.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button 
                  onClick={async () => {
                    if (deferredInstallPrompt) {
                      deferredInstallPrompt.prompt();
                      const { outcome } = await deferredInstallPrompt.userChoice;
                      if (outcome === 'accepted') setDeferredInstallPrompt(null);
                    } else {
                      alert('To install on Android:\n1. Open this page in Chrome\n2. Tap the 3-dot menu (...)\n3. Tap "Add to Home Screen"');
                    }
                  }}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded font-bold uppercase" data-testid="install-android-btn">
                  <Smartphone className="mr-2 w-4 h-4" />Android Install
                </Button>
                <Button 
                  onClick={() => {
                    alert('To install on iOS:\n1. Open this page in Safari\n2. Tap the Share button (box with arrow)\n3. Scroll down and tap "Add to Home Screen"');
                  }}
                  variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 rounded font-bold uppercase" data-testid="install-ios-btn">
                  <Smartphone className="mr-2 w-4 h-4" />iOS Add to Home
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chrome Extension Lead Magnet — operative only */}
      {mode !== 'executive' && (
      <section className="py-12 px-4 sm:px-6 lg:px-8 relative z-20">
        <div className="max-w-3xl mx-auto text-center">
          <div className="bg-card/50 border border-primary/20 rounded-lg p-6 sm:p-8 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent" />
            <div className="relative">
              <span className="text-xs font-mono text-primary tracking-widest">// INCOMING UPGRADE</span>
              <h3 className="font-display text-xl sm:text-2xl font-bold text-foreground mt-2 mb-3">
                Chrome Extension in Final Security Review
              </h3>
              <p className="text-sm text-muted-foreground mb-4 max-w-lg mx-auto">
                Our browser agent detects subscription traps and zombie trials while you browse.
                Currently in Google's security review process.
              </p>
              <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 rounded px-4 py-2">
                <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
                <span className="text-xs font-mono text-primary">DEPLOY ETA: PENDING GOOGLE CLEARANCE</span>
              </div>
              <p className="text-xs text-muted-foreground mt-4 font-mono">
                Shark Plan members get early access the moment it drops.
              </p>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ===== FINAL CTA ===== */}
      <section className={`py-20 sm:py-28 px-4 sm:px-6 lg:px-8 relative z-20 ${mode === 'executive' ? 'exec-gradient' : ''}`}>
        <div className="max-w-4xl mx-auto text-center">
          {mode === 'executive' ? (
            <>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mt-3 mb-4">
                Start Saving <span className="bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent">Today</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                Join thousands of smart subscribers who've already saved an average of $412/year. It takes 60 seconds and it's completely free to start.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg"
                  onClick={() => navigate(user ? '/dashboard' : '/register')}
                  className="bg-sky-500 text-white hover:bg-sky-600 rounded-2xl font-semibold text-lg px-12 py-6 shadow-xl shadow-sky-500/25"
                  data-testid="bottom-cta-btn"
                >
                  Get Started — Free
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-6">
                No credit card required. Cancel anytime.
              </p>
            </>
          ) : (
            <>
          <span className="text-xs font-mono text-destructive tracking-widest">// MISSION BRIEFING</span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mt-3 mb-4">
            Every Day You Wait, You're Losing Money.
          </h2>
          <p className="text-lg text-muted-foreground mb-4">
            Right now, there's a subscription you haven't used in weeks. A trial about to convert. A bill you could cut 20%.
          </p>
          <p className="text-lg text-primary font-bold mb-8">
            Sub-Zero fixes all of it. For less than the price of a coffee.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => navigate(user ? '/dashboard' : '/register')}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded font-bold uppercase tracking-wide text-lg px-12 glow-primary"
              data-testid="bottom-cta-btn"
            >
              <Target className="w-5 h-5 mr-2" />
              Deploy Your Agent
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate('/pricing')}
              className="border-white/20 text-foreground hover:bg-white/10 rounded font-mono"
              data-testid="bottom-pricing-btn"
            >
              VIEW PRICING
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-6 font-mono">
            Free scan. No credit card. Takes 60 seconds.
          </p>
            </>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className={`border-t py-8 px-4 sm:px-6 lg:px-8 relative z-20 ${mode === 'executive' ? 'border-gray-100 bg-white' : 'border-border'}`}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-2">
              {mode === 'executive' ? (
                <>
                  <div className="w-8 h-8 bg-gradient-to-br from-sky-500 to-blue-600 rounded-xl flex items-center justify-center">
                    <Zap className="w-4 h-4 text-white" />
                  </div>
                  <span className="font-display font-bold text-foreground">Sub-Zero</span>
                </>
              ) : (
                <>
                  <div className="w-8 h-8 bg-primary rounded flex items-center justify-center">
                    <Zap className="w-5 h-5 text-black" />
                  </div>
                  <span className="font-display font-bold text-foreground">SUB-ZERO</span>
                  <span className="text-xs font-mono text-muted-foreground">v2.0</span>
                </>
              )}
            </div>
            <div className={`flex items-center gap-4 sm:gap-6 text-sm text-muted-foreground flex-wrap justify-center ${mode === 'executive' ? 'font-medium' : 'font-mono'}`}>
              <Link to="/privacy" className="hover:text-foreground transition-colors">{mode === 'executive' ? 'Privacy' : '[PRIVACY]'}</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">{mode === 'executive' ? 'Terms' : '[TERMS]'}</Link>
              <Link to="/refund" className="hover:text-foreground transition-colors">{mode === 'executive' ? 'Refunds' : '[REFUND]'}</Link>
              <Link to="/faq" className="hover:text-foreground transition-colors">{mode === 'executive' ? 'FAQ' : '[FAQ]'}</Link>
              <Link to="/about" className="hover:text-foreground transition-colors">{mode === 'executive' ? 'About' : '[ABOUT]'}</Link>
              <Link to="/contact" className="hover:text-foreground transition-colors">{mode === 'executive' ? 'Contact' : '[CONTACT]'}</Link>
            </div>
          </div>
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 pt-4 border-t border-border/50">
            <p className={`text-xs text-muted-foreground ${mode === 'executive' ? '' : 'font-mono'}`}>
              &copy; 2026 {mode === 'executive' ? 'Sub-Zero' : 'SUB-ZERO OPERATIONS'} &mdash; The House Of Qami Ltd
            </p>
            <div className="flex items-center gap-4 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1"><Shield className="w-3 h-3 text-primary" /> {mode === 'executive' ? 'Bank-grade encryption' : '256-BIT ENCRYPTION'}</span>
              <span>support@usesubzero.com</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
